
import { WDModel, WDModelArray } from './wd-model.js';

export default class WDUtil {

  static isObject(item)
  {
    return (item !== null && typeof item === 'object' && !(item instanceof Array) );
  }

  /**
    * for the purpose of merging.  these aren't cloned
    */
  static isReference(item)
  {
    // Handle null/undefined first
    if (item === null || item === undefined) return false;
    
    return (	item instanceof Promise || 
              typeof(item) == 'function' || 
              item.constructor.toString().substring(0, 5) == 'class' ||
              item.isProxy ||
              item instanceof WDModel ||
              item instanceof WDModelArray
            );
  };

  /* From ChatGPT - need to test - also need to decide if it should clone or reference Proxies */
  static mergeObjects(target, ...sources)
  {
    if (!WDUtil.isObject(target))  throw new Error('Target must be an object');

    for (const source of sources) 
    {
      if (!WDUtil.isObject(source)) throw new Error('Source must be an object');

      for (const key in source) 
      {
        if (WDUtil.isObject(source[key])) 
        {
          if (!target[key] || !WDUtil.isObject(target[key])) 
          {
            target[key] = {};
          }
          WDUtil.mergeObjects(target[key], source[key]);
        } 
        else 
        {
          if (source[key]?.isProxy)
          {
            target[key] = source[key].toObject();
          }
          else if (WDUtil.isReference(source[key])) 
          {
            target[key] = source[key];
          } 
          else 
          {
            target[key] = JSON.parse(JSON.stringify(source[key]));
          }
        }
      }
    }
    
    return target;
  }


  /**
  * Deep merge two objects.
  * @param target
  * @param ...sources
  */
  static merge(target, ...sources)
  {
    if (!sources.length) return target;

    const source = sources.shift();

    if (WDUtil.isObject(target) && WDUtil.isObject(source)) 
    {
      for (const key in source) 
      {
        const sourceValue = source[key];
        
        // Skip undefined values
        if (sourceValue === undefined) continue;

        // Handle all reference types (including WDModel/WDModelArray subclasses)
        if (WDUtil.isReference(sourceValue))
        {
          target[key] = sourceValue;
        }
        // Handle nested objects
        else if (WDUtil.isObject(sourceValue)) 
        {
          // Target doesn't exist or is not an object, create new object
          if (!WDUtil.isObject(target[key])) {
            target[key] = {};
          }

          // Recursively merge the nested object
          WDUtil.merge(target[key], sourceValue);
        }
        // Handle arrays - clone them
        else if (Array.isArray(sourceValue))
        {
          target[key] = [...sourceValue]; // Clone the array
        }
        // Handle primitive values
        else
        {
          target[key] = sourceValue;
        }
      }
    }
    
    // Continue with remaining sources
    return WDUtil.merge(target, ...sources);
  };

  static extend(...items)
  {
    const target = items.shift();
    return this.merge(target,items);
  };

  static param(params)
  {
    if (!params) return '';

    const urlParams = new URLSearchParams();

    for (const [name, value] of Object.entries(params))
    {
      urlParams.append(name, value || '');
    }

    return urlParams.toString();

  };

  static uuid()
  {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      let r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };  

  static belowBreakpoint()
  {
    return (window.innerWidth < WDConfig.panes.breakpoint);
  };

  static fileToClass(fileName)
  {
    return fileName
      .split('-') // Split by hyphen
      .map((word, index) =>
        index === 0 && word.toLowerCase() === 'wd'
          ? 'WD' // Ensure 'WD' is always capitalized
          : word.charAt(0).toUpperCase() + word.slice(1) // Capitalize the rest
      )
      .join('');
  };

  static newClassToFile(className) {
    return className
        .replace(/([A-Z]+)([A-Z][a-z]|[a-z]|$)/g, (match, p1, p2) => {
            return `${p1.toLowerCase()}-${p2.toLowerCase()}`;
        })
        .replace(/-$/, ''); // Remove trailing hyphen if any
  };
  
  static classToFile(className)
  {
    const cn = className.replace('WD','wd');
    return cn
      .replace(/([A-Z])/g, '-$1') // Insert hyphen before each capital letter
      .toLowerCase() // Convert the whole string to lowercase
      .replace(/^-/, ''); // Remove leading hyphen if any
  }

  static isLDAPEnabled()
  {
    return (typeof(WDConfig.api.config.LDAPEnabled) != 'undefined' && WDConfig.api.config.LDAPEnabled == true);
  };

  static handleize(str)
  {
    return str
        .toLowerCase()                    // Convert to lowercase
        .normalize("NFD")                 // Normalize Unicode (decomposes accents)
        .replace(/[\u0300-\u036f]/g, "")  // Remove diacritics
        .replace(/[^a-z0-9]+/g, "-")      // Replace non-alphanumeric characters with hyphens
        .replace(/^-+|-+$/g, "");         // Trim leading/trailing hyphens
  };

  static matchRoute(routeURI,curURI)
  {
    //we have a match
    return typeof(URLPattern) != 'undefined' ? WDUtil.matchRouteDefault(routeURI,curURI) : WDUtil.matchRouteSafari(routeURI,curURI);
  };

  /**
    * handles route patterns with names, returning the values in an object
    * like /builder/:builderId/floorplans/:floorplanId returns
    * { builderId: 1, floorplanId:2 }
    */  
  static matchRouteDefault(routeURI,matchURI)
  {
    const pattern = new URLPattern(routeURI,window.location.origin);
    const match = pattern.exec(window.location.origin + matchURI);

    return match ? match.pathname.groups : null;
  };

  /**
    * I'm looking at you, safari
    */
  static matchRouteSafari(routeURI,matchURI)
  {
    // Convert :param to a regex named capture group (?<param>[^/]+)
    const regexPattern = routeURI.replace(/:([a-zA-Z_][a-zA-Z0-9_]*)/g, (_, paramName) => `(?<${paramName}>[^/]+)`);

    const patternStr = '^' + regexPattern + '$';
    const regex = new RegExp(patternStr);

    // Test the URL against the generated regex pattern
    const match = matchURI.match(regex);

    if (match)
    {
      //.groups is only there if there's a named pattern
      return match?.groups || match;
    }
    else
    {
      return null;
    }
  };

};

